import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Heart, Briefcase, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';

const GALLERY_IMAGES = [
  "https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/ob310o9q_IMG_1245.JPG",
  "https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/e8uqlyzh_IMG_1246.JPG",
  "https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/os8gywsu_IMG_1254.JPG",
  "https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/oaiqcby9_IMG_1275.JPG",
  "https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/mwkhvjao_IMG_1277.jpeg",
  "https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/vbecih5e_IMG_1602.jpg",
];

const impactPillars = [
  {
    icon: Shield,
    title: 'Safe Spaces & Belonging',
    description: 'Creating environments where people feel welcome, respected, and part of a community that cares.',
    color: '#E3B505',
    stats: '6+ safe space sessions delivered',
  },
  {
    icon: Users,
    title: 'Youth Engagement & Confidence',
    description: 'Empowering young people with opportunities to grow, learn, and develop skills for the future.',
    color: '#E76F51',
    stats: '500+ community members reached',
  },
  {
    icon: Heart,
    title: 'Community Wellbeing & Connection',
    description: 'Building networks of support that help families and individuals feel connected and supported.',
    color: '#264653',
    stats: '400k+ digital reach',
  },
  {
    icon: Briefcase,
    title: 'Partner-Supported Opportunities',
    description: 'Translating brand partnerships into real resources, experiences, and support for communities.',
    color: '#E3B505',
    stats: '4 partner organisations',
  },
];

const impactStats = [
  { number: 6, suffix: '+', label: 'Sessions Delivered' },
  { number: 4, suffix: '', label: 'Partner Organisations' },
  { number: 400, suffix: 'k', label: 'Digital Reach' },
  { number: 500, suffix: '+', label: 'Community Members Reached' },
];

const communityQuotes = [
  {
    quote: "The youth workshop gave my daughter confidence I hadn't seen before. She came home excited about her future for the first time.",
    author: "Parent, South London",
  },
  {
    quote: "This is what community should feel like. OVYC creates spaces where everyone is welcome, and you can feel it the moment you walk in.",
    author: "Community Member",
  },
  {
    quote: "As a young person, having a safe space to be creative and connect with others my age has been life-changing.",
    author: "Youth Programme Participant",
  },
];

// Counter animation hook
const useCounter = (target, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) return;
    
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [target, duration, shouldStart]);
  
  return count;
};

const StatCard = ({ stat, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const count = useCounter(stat.number, 2000, isVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-bold text-[#2D2A26] mb-2 font-['Manrope']">
        {count}{stat.suffix}
      </div>
      <div className="text-[#5C5852] text-sm">{stat.label}</div>
    </div>
  );
};

export const ImpactPage = () => {
  return (
    <main data-testid="impact-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-4 block">Our Impact</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#2D2A26] mb-6 font-['Manrope']">
              Real Change, Real Communities
            </h1>
            <p className="text-lg md:text-xl text-[#5C5852] leading-relaxed">
              Every event we deliver, every programme we run, and every partnership we build is 
              designed to create lasting positive change for the communities we serve.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Pillars */}
      <section 
        data-testid="pillars-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E76F51] mb-4 block">
              How We Create Change
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              Our Impact Pillars
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {impactPillars.map((pillar, index) => (
              <div 
                key={pillar.title}
                data-testid={`pillar-card-${index}`}
                className="bg-[#FAF9F6] rounded-3xl p-8 border border-[#E6E2D8]/50 hover:shadow-lg transition-all duration-300"
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${pillar.color}20` }}
                >
                  <pillar.icon className="w-7 h-7" style={{ color: pillar.color }} />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-[#2D2A26] mb-3 font-['Manrope']">
                  {pillar.title}
                </h3>
                <p className="text-[#5C5852] leading-relaxed mb-4">{pillar.description}</p>
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-[#E6E2D8]">
                  <span className="text-sm font-medium text-[#E3B505]">{pillar.stats}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section 
        data-testid="impact-stats-section"
        className="py-20 md:py-28 bg-[#E3B505]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              Impact in Numbers
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {impactStats.map((stat, index) => (
              <StatCard key={stat.label} stat={stat} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Community Quotes */}
      <section 
        data-testid="quotes-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#264653] mb-4 block">
              Community Voices
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#2D2A26] font-['Manrope']">
              What People Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {communityQuotes.map((item, index) => (
              <div 
                key={index}
                data-testid={`quote-card-${index}`}
                className="bg-[#FAF9F6] rounded-3xl p-8 relative"
              >
                <Quote className="w-8 h-8 text-[#E3B505]/30 mb-4" />
                <p className="text-[#2D2A26] leading-relaxed mb-6">
                  "{item.quote}"
                </p>
                <p className="text-sm font-medium text-[#5C5852]">— {item.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section 
        data-testid="gallery-section"
        className="py-20 md:py-28 bg-[#F4F1DE]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-4 block">
              In Action
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#2D2A26] font-['Manrope']">
              Community Moments
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {GALLERY_IMAGES.map((image, index) => (
              <div 
                key={index}
                className={`relative overflow-hidden rounded-2xl ${
                  index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                }`}
              >
                <img 
                  src={image}
                  alt={`Community moment ${index + 1}`}
                  className={`w-full object-cover hover:scale-105 transition-transform duration-500 ${
                    index === 0 ? 'h-[300px] md:h-full' : 'h-[200px] md:h-[250px]'
                  }`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#264653]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 font-['Manrope']">
            Be Part of Our Impact Story
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Partner with OVYC to create meaningful change in London communities. Together, we can 
            deliver more safe spaces, engage more young people, and build stronger support networks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/partner">
              <Button 
                data-testid="impact-partner-btn"
                className="bg-[#E3B505] text-[#2D2A26] hover:bg-[#E3B505]/90 rounded-full h-14 px-10 text-base font-medium shadow-lg"
              >
                Partner With Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/reports">
              <Button 
                data-testid="impact-reports-btn"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-full h-14 px-10 text-base font-medium"
              >
                View Our Reports
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ImpactPage;
